import find from "lodash-es/find";
import includes from "lodash-es/includes";
import isNil from "lodash-es/isNil";
import remove from "lodash-es/remove";
import { action, makeAutoObservable, runInAction } from "mobx";
import type { RefObject } from "react";
import { v4 as uuid } from "uuid";

import { apiClient } from "../common/apiClient";
import { STATICDATA_HOST } from "../common/environment";
import i18n from "../common/i18n";
import { logger } from "../common/logger";
import type { BaseStoreType } from "../common/types/BaseStoreType";
import type { SupportedLocaleType } from "../common/types/languageTypes";
import type { FlyingProductOrItemAnimationState } from "../common/types/productTypes";
import {
   getDataVersionFromStorage,
   getHasRefreshedFromStorage,
   setDataVersionInStorage,
   setHasRefreshedInStorage
} from "../common/utils/storage/storageUtils";
import theme from "../themes/theme";
import authStore from "./auth/authStore";
import loginState from "./auth/loginState";
import productStore from "./product/productStore";
import toastStore from "./toastStore";

export enum LOGIN_MODE {
   USERNAME = "username",
   SMS = "sms",
   RESET_PASSWORD_EMAIL = "resetPasswordEmail",
   RESET_PASSWORD_OTP = "resetPasswordOtp",
   RESET_PASSWORD_SUCCESS = "resetPasswordSuccess"
}

type UIStoreType = BaseStoreType & {
   planes: FlyingProductOrItemAnimationState[];
   dataVersion: string | null;
   newDataAvailable: boolean;
   locale: SupportedLocaleType;
   accessLevel: number;
   loginMode: LOGIN_MODE;
   showTranslationKeys: boolean;
   showDefaultTranslationText: boolean;
   showLanguageSwitcher: boolean;
   showLoggerModal: boolean;
   showAdventCalendarReminderSignupModal: boolean;

   ensureDataVersionAvailable(): Promise<void>;
   checkDataVersion(giveFeedback: boolean): Promise<void>;
   handleMissingDataFileError(err: Error): Promise<void>;
   takeOff(animationId: string, landingPadId?: string): void;
   addFlyingItem(fromRef: RefObject<HTMLImageElement | HTMLButtonElement | null>, landingPadId: string): void;
   addFlyingProduct(sku: string, fromRef: RefObject<HTMLImageElement | null>): void;
   handleShortcutKeypress(e: KeyboardEvent): void;
   switchLoginMode(newMode: LOGIN_MODE): void;
   showLoginModal(): void;
   hideLoginModal(): void;
   toggleLoggerModal(): void;
   toggleAdventCalendarReminderSignupModal(): void;
};

const uiStore: UIStoreType = makeAutoObservable({
   planes: [],
   dataVersion: getDataVersionFromStorage(),
   locale: i18n.language?.startsWith("en") ? "en-US" : "nb-NO",
   newDataAvailable: false,
   accessLevel: 0,
   loginMode: LOGIN_MODE.USERNAME,
   showTranslationKeys: false,
   showDefaultTranslationText: true,
   showLanguageSwitcher: false,
   showLoggerModal: false,
   showAdventCalendarReminderSignupModal: false,

   ensureDataVersionAvailable: async () => {
      if (uiStore.dataVersion === null) {
         // If we don't know a valid data version, just fetch it so its stored for next time.
         // No need to schedule an update check this session.
         return uiStore.checkDataVersion(false);
      }

      // If we already know a valid data version, we use that and schedule an update check
      // in 10 seconds once everything has settled.
      setTimeout(() => uiStore.checkDataVersion(false), 10000);
   },

   checkDataVersion: async (giveFeedback) => {
      const dataVersionUrl = `${STATICDATA_HOST}/${theme.storeId}/currentVersion.txt`;
      const latestDataVersion = await apiClient(dataVersionUrl).get().text();
      runInAction(() => {
         if (uiStore.dataVersion !== latestDataVersion) {
            logger.log("ui", `Static data available is version ${latestDataVersion}`);
            if (giveFeedback) {
               toastStore.addSuccess(
                  "Ny data tilgjengelig",
                  "Det er ny data tilgjengelig. Vennligst last siden på nytt for å få oppdatert informasjon."
               );
            }

            if (uiStore.dataVersion === null) {
               uiStore.dataVersion = latestDataVersion;
               setDataVersionInStorage(uiStore.dataVersion);
            } else {
               // If we detect that new data is available, we can remove the
               // data version stored in session storage. This ensures that the
               // next time the user visits the site, we will fetch the latest data.
               uiStore.newDataAvailable = true;
               setDataVersionInStorage(null);
            }
         } else {
            if (giveFeedback) {
               toastStore.addError("Ingen ny data tilgjengelig", "Det er ingen ny data tilgjengelig.");
            }
         }
      });
   },

   handleMissingDataFileError: async (err) => {
      logger.warn("ui", "An error occured while fetching data files!", err);

      // Check session storage to see if we have already tried to refresh, to avoid refresh loops
      const alreadyRefreshed = getHasRefreshedFromStorage();

      if (includes(err.message, "BlobNotFound") && !alreadyRefreshed) {
         setHasRefreshedInStorage(true);
         await uiStore.checkDataVersion(false);
         window.location.reload();
      } else {
         throw err;
      }
   },

   takeOff: (animationId, landingPadId = "mini-cart") => {
      const targetEl = document.getElementById(landingPadId);
      if (isNil(targetEl)) {
         return;
      }
      const rect = targetEl.getBoundingClientRect();

      const plane = find(uiStore.planes, { animationId });
      if (isNil(plane)) {
         return;
      }

      plane.x = rect.left + window.scrollX;
      plane.y = rect.top + window.scrollY;
      plane.height = targetEl.clientHeight;

      setTimeout(
         action(() => {
            remove(uiStore.planes, { animationId });
         }),
         1000
      );
   },

   addFlyingItem: (fromRef, landingPadId) => {
      const targetEl = document.getElementById(landingPadId);
      if (isNil(targetEl)) {
         logger.warn("ui", `Landing pad not found: ${landingPadId}`);
         return;
      }

      if (fromRef.current === null) {
         logger.warn("ui", "Attempted to fly image from undefined reference");
         return;
      }

      const el = fromRef.current;
      if (isNil(el)) {
         logger.warn("ui", `Unable to find current ref: ${JSON.stringify(fromRef)}`);
         return;
      }

      const rect = el.getBoundingClientRect();
      const animationId = uuid();

      uiStore.planes.push({
         animationId,
         x: rect.left + window.scrollX,
         y: rect.top + window.scrollY,
         sourceWidth: el instanceof HTMLImageElement ? el.naturalWidth : 8,
         height: el.clientHeight,
         landingPadId
      });
   },

   addFlyingProduct: (sku, fromRef) => {
      if (fromRef.current === null) {
         logger.warn("ui", "Attempted to fly image from undefined reference");
         return;
      }

      const el = fromRef.current;
      if (isNil(el)) {
         logger.warn("ui", `Unable to find current image for sku ${sku} in ref: ${JSON.stringify(fromRef)}`);
         return;
      }

      const rect = el.getBoundingClientRect();
      const animationId = uuid();

      uiStore.planes.push({
         animationId,
         sku,
         x: rect.left + window.scrollX,
         y: rect.top + window.scrollY,
         sourceWidth: el.naturalWidth,
         height: el.clientHeight
      });
   },

   handleShortcutKeypress: (e) => {
      // Toggle logger modal
      if (e.ctrlKey && e.shiftKey && e.code === "KeyL") {
         e.preventDefault();
         uiStore.toggleLoggerModal();
      }

      const isFunctionKeysHeld = e.altKey && e.ctrlKey && e.shiftKey;

      if (!isFunctionKeysHeld) {
         // The rest of this function should only run if user is holdeing
         // alt + ctrl + shift, so return if this is not the case.
         return;
      }

      if (e.code === "KeyO") {
         uiStore.showTranslationKeys = !uiStore.showTranslationKeys;
      }

      if (e.code === "KeyK" && uiStore.accessLevel === 0) {
         uiStore.accessLevel = 1;
      } else if (e.code === "KeyS" && uiStore.accessLevel === 1) {
         uiStore.accessLevel = 2;
         if (loginState.is("LOGGED_IN")) {
            loginState.transitionTo("NOT_LOGGED_IN");
         }
         productStore.assortment = productStore.products.map((p) => p.sku);
         toastStore.addSuccess("Du har nå KS-tilgang", "Du har blitt logget ut, men kan nå søke på alle produkter");
      }
   },

   switchLoginMode: (newMode) => {
      uiStore.loginMode = newMode;
   },

   showLoginModal: () => {
      if (loginState.is("START_UP")) {
         return;
      }
      loginState.transitionTo("LOGIN_MODAL");
   },

   toggleLoggerModal() {
      uiStore.showLoggerModal = !uiStore.showLoggerModal;
   },

   toggleAdventCalendarReminderSignupModal() {
      uiStore.showAdventCalendarReminderSignupModal = !uiStore.showAdventCalendarReminderSignupModal;
   },

   hideLoginModal: () => {
      loginState.transitionTo("NOT_LOGGED_IN");
      authStore.codeChallengeId = null;
      uiStore.switchLoginMode(LOGIN_MODE.USERNAME);
   },
   clearCompanySpecificData: () => {
      // add methods to clear company specific data
   }
} satisfies UIStoreType);

export default uiStore;
